<script lang="ts">
    import { getSeite, getPersonen } from "../api"
    import { apiBaseURL, baseURL } from "../config"
    import Breadcrumb from "./Breadcrumb.svelte"
    import PersonTeaser from "./PersonTeaser.svelte"
    import Slider from "./Slider/Slider.svelte"
    import Fehler404 from "./Fehler404.svelte"
    import LadeAnimation from "./LadeAnimation.svelte"
    import { i18n } from "../store"
    import { scrollTo } from "svelte-scrollto"
    import DownloadTeaser from "./DownloadTeaser.svelte"
    import { previewEntryMap } from "../preview"
    import PagebuilderRow from "./PagebuilderRow.svelte"
    import Module from "./Module"
    import { shortcodeComponents } from "./Shortcodes"
    import ContentBuilder from "./ContentBuilder.svelte"

    export let pfad: string
    export let preview = false

    let seite: Seite
    let personen: Person[]
    let lade: boolean = true

    $: if ($previewEntryMap["seiten"]) {
        lade = false
        seite = $previewEntryMap["seiten"]
        $previewEntryMap["seiten"] = null
    }

    let sprungpunkte: { idx: number; titel: string }[]

    async function ladeSeite(pfad: string) {
        lade = true
        seite = await getSeite(pfad)
        let _spruenge = []
        seite?.pagebuilder?.forEach((p, idx) => {
            if (p.titelSprung) {
                _spruenge.push({ idx, titel: p.titel })
            }
        })
        sprungpunkte = _spruenge

        if (seite?.kontaktpersonen) {
            personen = await getPersonen(
                false,
                seite.kontaktpersonen.split(/[,\s]+/)
            )
        }

        lade = false
    }

    $: if (!preview) ladeSeite(pfad)

    const fullBaseURL = apiBaseURL.startsWith("http")
        ? apiBaseURL
        : baseURL + apiBaseURL
</script>

<svelte:head>
    {#key seite}
        <meta property="og:type" content="article" />
        <meta
            property="og:url"
            content="{fullBaseURL + (seite?.pfad || pfad)}"
        />

        <!-- Title -->
        {#if seite?.meta?.titel}
            <title>{seite.meta.titel}</title>
            <meta property="og:title" content="{seite.meta.titel}" />
        {:else if seite?.titel}
            <title>{seite.titel}</title>
            <meta property="og:title" content="{seite.titel}" />
        {:else}
            <title>Stiftung Familienunternehmen und Politik</title>
            <meta
                property="og:title"
                content="Stiftung Familienunternehmen und Politik"
            />
        {/if}

        <!-- Description -->
        {#if seite?.meta?.beschreibung}
            <meta name="description" content="{seite.meta.beschreibung}" />
            <meta
                property="og:description"
                content="{seite.meta.beschreibung}"
            />
        {/if}

        {#if seite?.bild3?.src}
            <meta
                property="og:image"
                content="{fullBaseURL +
                    'seiten/' +
                    seite.id +
                    '/' +
                    seite.bild3.src}"
            />
        {:else if seite?.bild2?.src}
            <meta
                property="og:image"
                content="{fullBaseURL +
                    'seiten/' +
                    seite.id +
                    '/' +
                    seite.bild2.src}"
            />
        {:else if seite?.bild?.src}
            <meta
                property="og:image"
                content="{fullBaseURL +
                    'seiten/' +
                    seite.id +
                    '/' +
                    seite.bild.src}"
            />
        {:else if seite?.slider?.length > 0}
            <meta
                property="og:image"
                content="{fullBaseURL +
                    'seiten/' +
                    seite.id +
                    '/' +
                    seite.slider[0].bild.src}"
            />
        {/if}

        <!-- Keywords -->
        {#if seite?.meta?.keywords}
            <meta name="keywords" content="{seite.meta.keywords}" />
        {:else}
            <meta
                name="keywords"
                content="stiftung familienunternehmen und politik, stiftung familienunternehmen, politik und wirtschaft, haus des familienunternehmens"
            />
        {/if}

        {#if seite?.aktiv === false}
            <meta name="robots" content="noindex" />
        {/if}

        {#if seite?.pfad && seite.pfad != pfad}
            <link
                rel="canonical"
                href="{seite.pfad != '/' ? baseURL + seite.pfad : '/'}"
            />
        {/if}
    {/key}
</svelte:head>

{#if lade}
    <LadeAnimation />
{:else}
    {#if seite?.slider?.length > 0}
        <div id="headerpic">
            {#if seite.slider.length > 1}
                <Slider
                    slides="{seite.slider.map((s) => ({
                        title: s.text1,
                        cat: s.hashtag,
                        button_link: s.mehrURL,
                        button_text: s.mehrURL ? $i18n('mehr') : '',
                        image: !s.bild?.src?.startsWith('data:')
                            ? apiBaseURL +
                              'seiten/' +
                              seite.id +
                              '/' +
                              s.bild?.src +
                              '?filter=xl'
                            : s.bild?.src,
                    }))}"
                    dots="{true}"
                    arrows="{true}"
                    effect="fade"
                    type="heroslider"
                    template="standard"
                />
            {:else}
                <div class="image">
                    <img
                        src="{!seite.slider[0].bild?.src?.startsWith('data:')
                            ? apiBaseURL +
                              'seiten/' +
                              seite.id +
                              '/' +
                              seite.slider[0].bild?.src +
                              '?filter=xl'
                            : seite.slider[0].bild?.src}"
                        alt=""
                    />
                </div>
                <div class="text">
                    {#if seite.slider[0].hashtag}
                        <div class="cat">{seite.slider[0].hashtag}</div>
                        <br />
                    {/if}
                    {#if seite.slider[0].text1}
                        <div class="headline">
                            <h1>{seite.slider[0].text1}</h1>
                        </div>
                    {/if}
                    {#if seite.slider[0].text2}
                        <div class="subline">{seite.slider[0].text2}</div>
                    {/if}
                </div>
            {/if}
        </div>
    {/if}

    {#if seite?.titel || seite?.pagebuilder?.length}
        <section class="section_padding subpage">
            <div class="container">
                <Breadcrumb seite="{seite}" />

                {#if seite.meta?.datum}
                    <div class="date">
                        {new Date(seite.meta.datum).toLocaleDateString(
                            pfad?.startsWith("en/") ? "en-US" : "de-DE",
                            {
                                year: "numeric",
                                month: "long",
                            }
                        )}
                    </div>
                {/if}
                {#if seite.meta?.autor}
                    <div class="author">{seite.meta.autor}</div>
                {/if}

                {#if seite?.titel}
                    <h1>{seite.titel}</h1>
                {/if}
                {#if seite?.contentbuilder}
                    <ContentBuilder html="{seite.contentbuilder}" />
                {/if}
                {#if seite?.pagebuilder?.length}
                    {#each seite.pagebuilder as row, idx}
                        <PagebuilderRow
                            row="{row}"
                            idx="{idx}"
                            sprungpunkte="{sprungpunkte}"
                            scrollToElement="{scrollTo}"
                            imgBasePath="{apiBaseURL + 'seiten/' + seite.id}"
                            _i18n="{$i18n}"
                            Module="{Module}"
                            shortcodeComponents="{shortcodeComponents}"
                        />
                    {/each}
                {/if}
                {#if seite?.fussnote}
                    <div class="whitebox">
                        {@html seite.fussnote}
                    </div>
                {/if}
            </div>
        </section>

        {#if seite?.downloads?.length}
            <section class="section_padding">
                <div class="container">
                    <h3>{$i18n("Weitere Informationen")}</h3>
                    <div class="downloads">
                        {#each seite.downloads as d}
                            <DownloadTeaser
                                item="{{
                                    title: d.titel,
                                    cat: d.hashtag,
                                    content: d.teaserText,
                                    image: !d.bild?.src?.startsWith('data:')
                                        ? apiBaseURL +
                                          'seiten/' +
                                          seite.id +
                                          '/' +
                                          d.bild?.src +
                                          '?filter=m'
                                        : d.bild?.src,
                                    link:
                                        apiBaseURL +
                                        'seiten/' +
                                        seite.id +
                                        '/' +
                                        d.datei?.src,
                                }}"
                            />
                        {/each}
                    </div>
                </div>
            </section>
        {/if}
    {/if}

    {#if personen?.length}
        <div id="ansprechpartner">
            <div class="container">
                <h3>{$i18n("Kontakt")}</h3>
                <div class="list">
                    {#each personen || [] as person}
                        <PersonTeaser person="{person}" />
                    {/each}
                </div>
            </div>
        </div>
    {/if}

    {#if !seite}
        <Fehler404 />
    {/if}
{/if}

<style lang="less">
    @import "../css/base.less";

    .subpage {
        h1 {
            font-size: 2rem;
            font-weight: 400;
            @media (max-width: 768px) {
                font-size: 1.6rem;
            }
            &:before {
                content: "";
                height: 100%;
                background: #ba2c2b;
                border-left: solid 4px #ba2c2b;
                margin-right: 20px;
            }
        }
    }

    .downloads :global {
        .flex();
        margin: -15px;
        flex-wrap: wrap;
        .post {
            padding: 15px;
            .flex();
            flex-direction: column;
            .item {
                flex-grow: 1;
            }
        }
    }

    #ansprechpartner {
        padding: 60px 0px;
        background: rgb(213, 213, 213);
        background: linear-gradient(
            180deg,
            rgba(213, 213, 213, 1) 0%,
            rgba(231, 231, 231, 1) 50%
        );
        h3 {
            font-weight: 400;
            margin-bottom: 40px;
        }
        .list {
            .flex();
            flex-wrap: wrap;
            margin: -15px;
        }
    }

    #headerpic {
        position: relative;
        -webkit-box-shadow: 0px 10px 20px 3px rgba(0, 0, 0, 0.2);
        box-shadow: 0px 10px 20px 3px rgba(0, 0, 0, 0.2);
        width: 100%;
        max-width: 1600px;
        padding: 0px;
        margin: 0 auto 0 auto;

        .image {
            line-height: 0;
            width: 100%;
            @media (max-width: 860px) {
                height: 400px;
            }
            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }
        .text {
            position: absolute;
            bottom: 40px;
            left: 60px;
            right: 60px;
            margin: 0px;
            @media (max-width: 600px) {
                left: 20px;
                right: 20px;
            }
            .headline {
                background: #fff;
                padding: 10px 20px;
                width: auto;
                display: inline-block;
                margin-bottom: 10px;
            }
            .subline {
                padding: 5px 20px;
                background: #333;
                color: #fff;
                font-weight: 700;
                display: inline-block;
            }
            .cat {
                background-color: #878787;
                padding: 5px 20px;
                color: #fff;
                font-weight: 600;
                display: inline-block;
                font-size: 0.9rem;
                margin-bottom: 10px;
            }
            h1 {
                font-size: 2.2rem;
                margin: 0px;
                padding: 0px;
                border: 0;
                @media (max-width: 1060px) {
                    font-size: 1.6rem;
                }
                @media (max-width: 600px) {
                    font-size: 1.2rem;
                }
            }
        }
    }

    .date {
        color: #878787;
        font-weight: 700;
        font-size: 0.9rem;
        margin-bottom: 5px;
    }
    .author {
        font-weight: 700;
        font-size: 1.1rem;
    }

    .whitebox {
        background-color: white;
        font-size: 0.9em;
        box-shadow: 0 0 40px 0 rgba(51, 51, 51, 0.2);
        padding: 25px;
    }
</style>
